import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/common/Header';
import api from '../services/api';
import { RefreshCw, Target, DollarSign, AlertCircle, CheckCircle, XCircle, Building2, TestTube, Link as LinkIcon, HelpCircle, X, Plus, Cloud, Zap, Clock, Activity, Search, Filter, Eye, EyeOff } from 'lucide-react';

interface Campaign {
  id: number;
  google_campaign_id: string;
  campaign_name: string;
  status: string;
  budget_micros: number;
  sync_enabled: boolean;
  last_synced_at: string | null;
}

interface GoogleAccount {
  accountId: number;
  customerId: string;
  mccId: string | null;
  displayName: string;
  status: string;
  campaigns: Campaign[];
}

export default function Campaigns() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<GoogleAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [googleConnected, setGoogleConnected] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [campaignRules, setCampaignRules] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showInactive, setShowInactive] = useState(false); // Default: hide inactive campaigns
  const [searchQuery, setSearchQuery] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [discoveryMessage, setDiscoveryMessage] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadCampaigns();
    }
  }, [isAuthenticated]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/campaigns');

      // Handle new API format with accounts array
      if (response.data.accounts) {
        setAccounts(response.data.accounts || []);
      } else {
        // Fallback for old format
        setAccounts([{
          accountId: 0,
          customerId: '',
          mccId: null,
          displayName: 'Default Account',
          status: 'active',
          campaigns: response.data.campaigns || [],
        }]);
      }

      setGoogleConnected(true);
    } catch (err: any) {
      console.error('Failed to load campaigns:', err);
      if (err.response?.status === 401 || err.response?.data?.error?.includes('Google Ads not connected')) {
        setGoogleConnected(false);
        setError('Google Ads account not connected. Please connect in Settings.');
      } else {
        setError(err.response?.data?.error || 'Failed to load campaigns');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError('');
      await api.post('/campaigns/sync');
      // Reload campaigns after successful sync
      await loadCampaigns();
    } catch (err: any) {
      console.error('Sync failed:', err);
      if (err.response?.status === 401 || err.response?.data?.error?.includes('Google Ads not connected')) {
        setGoogleConnected(false);
        setError('Google Ads account not connected. Please connect in Settings first.');
      } else {
        setError(err.response?.data?.error || 'Failed to sync campaigns');
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleDiscoverAccounts = async () => {
    try {
      setDiscovering(true);
      setError('');
      setDiscoveryMessage('');
      const response = await api.post('/campaigns/discover-accounts');
      if (response.data.success) {
        setDiscoveryMessage(`✅ Discovered ${response.data.totalCount} accounts (${response.data.addedCount} new, ${response.data.updatedCount} updated)`);
        // Reload campaigns to show newly discovered accounts
        await loadCampaigns();
      }
    } catch (err: any) {
      console.error('Account discovery failed:', err);
      setError(err.response?.data?.error || 'Failed to discover accounts');
    } finally {
      setDiscovering(false);
    }
  };

  const formatBudget = (micros: number | null | undefined) => {
    // Convert to number and validate
    const numMicros = Number(micros);
    if (!micros || isNaN(numMicros) || !isFinite(numMicros) || numMicros < 0) return 'R0.00';

    // Check for overflow (> 1 trillion micros = R1 million/day)
    if (numMicros > 1_000_000_000_000) {
      console.error('Budget overflow detected:', micros);
      return 'R0.00 (ERROR)';
    }

    const value = numMicros / 1_000_000;
    if (!isFinite(value)) return 'R0.00';
    return `R${value.toFixed(2)}`;
  };

  const handleOpenCampaignDetails = async (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailsModal(true);
    setLoadingDetails(true);
    setCampaignRules([]);

    try {
      // Fetch weather rules for this campaign
      const response = await api.get('/rules');
      if (response.data.success && response.data.rules) {
        const campaignRulesData = response.data.rules.filter(
          (rule: any) => rule.campaign_name === campaign.campaign_name
        );
        setCampaignRules(campaignRulesData);
      }
    } catch (err) {
      console.error('Failed to fetch campaign rules:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Map Google Ads status codes to readable text
  const getStatusText = (status: string | number): string => {
    const statusStr = String(status).toUpperCase();
    // Google Ads API status enum: 2=ENABLED, 3=PAUSED, 4=REMOVED
    if (statusStr === '2') return 'ENABLED';
    if (statusStr === '3') return 'PAUSED';
    if (statusStr === '4') return 'REMOVED';
    return statusStr; // Return as-is if already text
  };

  const getStatusColor = (status: string | number) => {
    const statusText = getStatusText(status);
    switch (statusText) {
      case 'ENABLED':
        return 'text-green-400';
      case 'PAUSED':
        return 'text-amber-400';
      case 'REMOVED':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string | number) => {
    const statusText = getStatusText(status);
    switch (statusText) {
      case 'ENABLED':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'PAUSED':
        return <AlertCircle className="w-5 h-5 text-amber-400" />;
      case 'REMOVED':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getAccountIcon = (account: GoogleAccount) => {
    if (account.mccId) {
      return <Building2 className="w-5 h-5 text-cyan-400" />;
    }
    return <TestTube className="w-5 h-5 text-purple-400" />;
  };

  const getAccountBadge = (account: GoogleAccount) => {
    if (account.mccId) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs font-medium rounded-full">
          <Building2 className="w-3 h-3" />
          MCC Client
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 text-purple-400 text-xs font-medium rounded-full">
        <TestTube className="w-3 h-3" />
        Test Account
      </span>
    );
  };

  // 🎨 Template Detection & Color Coding (from Rules.tsx)
  const detectTemplate = (ruleName: string): { template: string; color: string; label: string } | null => {
    const name = ruleName.toLowerCase();

    // Complete Year-Round Template (8 rules total)
    if (
      name.includes('≥95%') || name.includes('≥80%') || name.includes('≥60%') || name.includes('≥40%') ||
      name.includes('very dry') ||
      name.includes('summer baseline') || name.includes('winter baseline') ||
      (name.includes('clear skies') && name.includes('<10%'))
    ) {
      return { template: 'complete_yearround', color: 'purple', label: '💎 Complete Year-Round' };
    }

    // Rain-Based Template (6 rules total)
    if (
      name.includes('≥90%') || name.includes('≥70%') || name.includes('≥50%') ||
      name.includes('low rain') || name.includes('very low rain') ||
      (name.includes('clear skies') && name.includes('<5%'))
    ) {
      return { template: 'rain_tiered', color: 'blue', label: '🌧️ Rain-Based' };
    }

    // Temperature Template (has °C)
    if (name.includes('°c') || name.includes('heat') || name.includes('freezing') || name.includes('cold day') || name.includes('hot day')) {
      return { template: 'temperature_seasonal', color: 'orange', label: '🌡️ Temperature' };
    }

    // Wind Template (has km/h)
    if (name.includes('km/h') || name.includes('wind')) {
      return { template: 'wind_emergency', color: 'cyan', label: '💨 Wind Emergency' };
    }

    return null; // Custom rule
  };

  const getTemplateStyles = (template: { template: string; color: string; label: string } | null) => {
    if (!template) return { border: 'border-gray-700', badge: null, bg: '' };

    const colorMap: Record<string, { border: string; badge: string; bg: string }> = {
      purple: {
        border: 'border-purple-500/50 hover:border-purple-500',
        badge: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
        bg: 'bg-gradient-to-r from-purple-900/20 to-indigo-900/20'
      },
      blue: {
        border: 'border-blue-500/50 hover:border-blue-500',
        badge: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
        bg: 'bg-gradient-to-r from-blue-900/20 to-cyan-900/20'
      },
      orange: {
        border: 'border-orange-500/50 hover:border-orange-500',
        badge: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
        bg: 'bg-gradient-to-r from-orange-900/20 to-amber-900/20'
      },
      cyan: {
        border: 'border-cyan-500/50 hover:border-cyan-500',
        badge: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
        bg: 'bg-gradient-to-r from-cyan-900/20 to-teal-900/20'
      },
    };

    return colorMap[template.color];
  };

  // Get unique color scheme for each account
  const getAccountColorScheme = (index: number) => {
    const colorSchemes = [
      {
        border: 'border-cyan-500/50',
        headerGradient: 'from-cyan-900/40 to-cyan-800/40',
        accent: 'text-cyan-400',
        iconBg: 'bg-cyan-500/20',
        campaignHover: 'hover:bg-cyan-900/20',
      },
      {
        border: 'border-purple-500/50',
        headerGradient: 'from-purple-900/40 to-purple-800/40',
        accent: 'text-purple-400',
        iconBg: 'bg-purple-500/20',
        campaignHover: 'hover:bg-purple-900/20',
      },
      {
        border: 'border-emerald-500/50',
        headerGradient: 'from-emerald-900/40 to-emerald-800/40',
        accent: 'text-emerald-400',
        iconBg: 'bg-emerald-500/20',
        campaignHover: 'hover:bg-emerald-900/20',
      },
      {
        border: 'border-orange-500/50',
        headerGradient: 'from-orange-900/40 to-orange-800/40',
        accent: 'text-orange-400',
        iconBg: 'bg-orange-500/20',
        campaignHover: 'hover:bg-orange-900/20',
      },
      {
        border: 'border-pink-500/50',
        headerGradient: 'from-pink-900/40 to-pink-800/40',
        accent: 'text-pink-400',
        iconBg: 'bg-pink-500/20',
        campaignHover: 'hover:bg-pink-900/20',
      },
    ];
    return colorSchemes[index % colorSchemes.length];
  };

  // Filter campaigns function
  const filterCampaigns = (campaigns: Campaign[]) => {
    return campaigns.filter(campaign => {
      // Filter by status (show/hide inactive)
      const status = getStatusText(campaign.status);
      if (!showInactive && status !== 'ENABLED') {
        return false;
      }

      // Filter by search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return (
          campaign.campaign_name.toLowerCase().includes(query) ||
          campaign.google_campaign_id.toLowerCase().includes(query)
        );
      }

      return true;
    });
  };

  // Calculate total stats across all accounts (unfiltered)
  const totalCampaigns = accounts.reduce((sum, acc) => sum + acc.campaigns.length, 0);
  const activeCampaigns = accounts.reduce(
    (sum, acc) => sum + acc.campaigns.filter(c => getStatusText(c.status) === 'ENABLED').length,
    0
  );
  const pausedCampaigns = accounts.reduce(
    (sum, acc) => sum + acc.campaigns.filter(c => getStatusText(c.status) === 'PAUSED').length,
    0
  );
  const totalBudget = accounts.reduce(
    (sum, acc) => {
      const accountBudget = acc.campaigns.reduce((s, c) => {
        // Explicit Number() conversion to prevent string concatenation bug
        const budget = Number(c.budget_micros) || 0;
        // Validate: must be positive, finite, and not overflow
        if (isNaN(budget) || !isFinite(budget) || budget < 0 || budget > 1_000_000_000_000) {
          return s;
        }
        const newSum = s + budget;
        // Check for overflow during accumulation
        if (!isFinite(newSum) || newSum > 1_000_000_000_000) {
          console.error('Total budget overflow detected:', { account: acc.displayName, budget });
          return s;
        }
        return newSum;
      }, 0);
      return sum + accountBudget;
    },
    0
  );

  // Calculate filtered stats
  const filteredTotalCampaigns = accounts.reduce(
    (sum, acc) => sum + filterCampaigns(acc.campaigns).length,
    0
  );

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-white">Campaigns</h2>
            <p className="text-gray-400 mt-1">Manage your Google Ads campaigns across {accounts.length} account{accounts.length !== 1 ? 's' : ''}</p>
          </div>
          {googleConnected && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/settings#google-accounts')}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold rounded-lg transition-all"
              >
                <Plus className="w-5 h-5" />
                Connect Another Account
              </button>
              <div className="relative group">
                <button
                  onClick={handleDiscoverAccounts}
                  disabled={discovering}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold rounded-lg transition-all disabled:opacity-50"
                >
                  <Search className={`w-5 h-5 ${discovering ? 'animate-pulse' : ''}`} />
                  {discovering ? 'Discovering...' : 'Discover Accounts'}
                </button>
                <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 border border-emerald-700 rounded-lg text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[9999] w-72">
                  <p className="whitespace-normal">Finds all Google Ads accounts you have access to under your connected MCC. Useful for discovering client accounts you haven't added yet.</p>
                </div>
              </div>
              <button
                onClick={() => setShowHelpModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all"
              >
                <HelpCircle className="w-5 h-5" />
                Help
              </button>
              <div className="relative group">
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold rounded-lg transition-all disabled:opacity-50"
                >
                  <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing...' : 'Sync from Google Ads'}
                </button>
                <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 border border-cyan-700 rounded-lg text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[9999] w-64">
                  <p className="whitespace-normal">Fetches latest campaign data from your Google Ads account (name, status, budget, etc.)</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-900/30 border border-red-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-300">{error}</p>
                {!googleConnected && (
                  <button
                    onClick={() => navigate('/settings')}
                    className="mt-2 text-sm text-cyan-400 hover:text-cyan-300 underline"
                  >
                    Go to Settings to connect Google Ads
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {discoveryMessage && (
          <div className="mb-6 bg-emerald-900/30 border border-emerald-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-emerald-300">{discoveryMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Google Not Connected State */}
        {!loading && !googleConnected && (
          <div className="bg-gradient-to-r from-blue-900/30 to-cyan-900/30 border-2 border-cyan-500/50 rounded-lg p-8 text-center">
            <Target className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Connect Your Google Ads Account</h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              To view and manage your campaigns, you need to connect your Google Ads account first.
              This will allow the system to sync your campaigns and automate bid adjustments based on weather conditions.
            </p>
            <button
              onClick={() => navigate('/settings')}
              className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold rounded-lg transition-all"
            >
              Go to Settings to Connect
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-12 text-center">
            <RefreshCw className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-300">Loading campaigns...</p>
          </div>
        )}

        {/* No Campaigns State */}
        {!loading && googleConnected && totalCampaigns === 0 && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center">
            <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Campaigns Found</h3>
            <p className="text-gray-400 mb-4">
              No campaigns have been synced yet. Click the "Sync from Google Ads" button to import your campaigns.
            </p>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold rounded-lg transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 inline mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Campaigns'}
            </button>
          </div>
        )}

        {/* Campaigns List (Grouped by Account) */}
        {!loading && googleConnected && totalCampaigns > 0 && (
          <div className="space-y-8">
            {/* Overall Stats Summary */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Overall Statistics</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-500/20 rounded-lg">
                    <Target className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Total Campaigns</p>
                    <p className="text-2xl font-bold text-white">{totalCampaigns}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Active</p>
                    <p className="text-2xl font-bold text-white">{activeCampaigns}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-500/20 rounded-lg">
                    <AlertCircle className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Paused</p>
                    <p className="text-2xl font-bold text-white">{pausedCampaigns}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-cyan-500/20 rounded-lg">
                    <DollarSign className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Total Budget</p>
                    <p className="text-2xl font-bold text-white">{formatBudget(totalBudget)}/day</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter & Search Bar */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                {/* Search Bar */}
                <div className="flex-1 w-full sm:w-auto">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search campaigns by name or ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Toggle Inactive Campaigns - Prominent Version */}
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">
                      Showing {filteredTotalCampaigns} of {totalCampaigns} campaigns
                    </span>
                  </div>

                  {/* Prominent Toggle Switch */}
                  <label className="flex items-center gap-3 cursor-pointer bg-gradient-to-r from-amber-900/40 to-yellow-900/40 border-2 border-amber-600/60 hover:border-amber-500 rounded-xl px-4 py-2.5 transition-all hover:scale-105 shadow-lg">
                    {/* Custom Toggle Switch */}
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-14 h-7 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-500/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-amber-600"></div>
                    </div>

                    {/* Label Text */}
                    <span className="text-base font-semibold text-white flex items-center gap-2">
                      {showInactive ? (
                        <>
                          <Eye className="w-5 h-5 text-green-400" />
                          <span>Show Inactive</span>
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-5 h-5 text-amber-400" />
                          <span>Hide Inactive <span className="text-amber-300">({pausedCampaigns})</span></span>
                        </>
                      )}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Campaigns Grouped by Account */}
            {accounts.map((account, accountIndex) => {
              const filteredCampaigns = filterCampaigns(account.campaigns);

              // Skip rendering this account if no campaigns match the filter
              if (filteredCampaigns.length === 0) {
                return null;
              }

              const colorScheme = getAccountColorScheme(accountIndex);
              return (
              <div key={account.customerId} className={`bg-gray-800 border-2 ${colorScheme.border} rounded-lg shadow-lg`}>
                {/* Account Header */}
                <div className={`bg-gradient-to-r ${colorScheme.headerGradient} p-5 border-b-2 ${colorScheme.border}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getAccountIcon(account)}
                      <div>
                        <h3 className="text-lg font-bold text-white">{account.displayName}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm text-gray-400">Customer ID: {account.customerId}</p>
                          {account.mccId && (
                            <p className="text-sm text-gray-400">• MCC: {account.mccId}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getAccountBadge(account)}
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                        account.status === 'active'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-700 text-gray-400'
                      }`}>
                        {account.status === 'active' ? '● Connected' : '○ Pending'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Campaigns Table */}
                <div className="overflow-x-auto overflow-y-visible">
                  <table className="w-full">
                    <thead className="bg-gray-900 relative z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Campaign
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Daily Budget
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Synced
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Last Synced
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {filteredCampaigns.map((campaign) => (
                        <tr key={campaign.id} className={`${colorScheme.campaignHover} transition-colors`}>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleOpenCampaignDetails(campaign)}
                              className="flex items-center gap-3 w-full text-left hover:opacity-80 transition-opacity group"
                            >
                              <div className={`p-2 ${colorScheme.iconBg} rounded-lg group-hover:scale-110 transition-transform`}>
                                <Target className={`w-5 h-5 ${colorScheme.accent} flex-shrink-0`} />
                              </div>
                              <div>
                                <p className="font-medium text-white group-hover:text-cyan-400 transition-colors cursor-pointer">
                                  {campaign.campaign_name}
                                </p>
                                <p className="text-xs text-gray-400">ID: {campaign.google_campaign_id}</p>
                              </div>
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(campaign.status)}
                              <span className={`font-medium ${getStatusColor(campaign.status)}`}>
                                {getStatusText(campaign.status)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4 text-emerald-400" />
                              <span className="font-medium text-white">{formatBudget(campaign.budget_micros)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {campaign.sync_enabled ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
                                <CheckCircle className="w-3 h-3" />
                                Yes
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-700 text-gray-400 text-xs font-medium rounded-full">
                                <XCircle className="w-3 h-3" />
                                No
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-400">
                              {campaign.last_synced_at
                                ? new Date(campaign.last_synced_at).toLocaleString()
                                : 'Never'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Empty State for Accounts with No Campaigns */}
                {account.campaigns.length === 0 && (
                  <div className="p-8 text-center">
                    {account.status === 'pending_connection' ? (
                      <>
                        <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-white mb-2">Connection Required</h3>
                        <p className="text-gray-400 mb-4">
                          This account needs to be connected via Google OAuth before campaigns can be synced.
                        </p>
                        <button
                          onClick={() => {
                            const userId = localStorage.getItem('userId');
                            const url = `/api/auth/google?userId=${userId}&accountId=${account.accountId}`;
                            window.location.href = 'https://weather.team4u.co.za' + url;
                          }}
                          className="px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-bold rounded-lg transition-all inline-flex items-center gap-2"
                        >
                          <LinkIcon className="w-5 h-5" />
                          Connect Google Ads
                        </button>
                        <p className="text-xs text-gray-500 mt-3">
                          You'll be redirected to Google to authorize access for Customer ID: {account.customerId}
                        </p>
                      </>
                    ) : (
                      <>
                        <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-white mb-2">No Campaigns Yet</h3>
                        <p className="text-gray-400 mb-4">
                          No campaigns found for this account. Click "Sync from Google Ads" to import campaigns.
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Account Footer */}
                <div className="bg-gray-900/50 px-6 py-3 border-t border-gray-700">
                  <p className="text-sm text-gray-400">
                    {account.campaigns.length} campaign{account.campaigns.length !== 1 ? 's' : ''} •
                    Total Budget: {formatBudget(account.campaigns.reduce((sum, c) => {
                      // Explicit Number() conversion to prevent string concatenation bug
                      const budget = Number(c.budget_micros) || 0;
                      // Validate each budget value
                      if (isNaN(budget) || !isFinite(budget) || budget < 0) return sum;
                      // Check for overflow during accumulation
                      const newSum = sum + budget;
                      if (!isFinite(newSum) || newSum > 1_000_000_000_000) {
                        console.error('Budget sum overflow detected:', { sum, budget, campaign: c.campaign_name });
                        return sum; // Skip this campaign to prevent corruption
                      }
                      return newSum;
                    }, 0))}/day
                  </p>
                </div>
              </div>
              );
            })}

            {/* Empty State - No Campaigns Match Filter */}
            {filteredTotalCampaigns === 0 && (
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-12 text-center">
                <Filter className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No Campaigns Match Your Filter</h3>
                <p className="text-gray-400 mb-4">
                  {searchQuery
                    ? `No campaigns found matching "${searchQuery}"`
                    : `All ${pausedCampaigns} paused campaigns are hidden`}
                </p>
                <div className="flex gap-3 justify-center">
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-all"
                    >
                      Clear Search
                    </button>
                  )}
                  {!showInactive && pausedCampaigns > 0 && (
                    <button
                      onClick={() => setShowInactive(true)}
                      className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg transition-all flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Show {pausedCampaigns} Inactive Campaign{pausedCampaigns !== 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Info Box */}
        {!loading && googleConnected && totalCampaigns > 0 && (
          <div className="mt-6 bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border border-cyan-500/30 rounded-lg p-5">
            <div className="flex items-start gap-3">
              <RefreshCw className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-cyan-200 font-semibold text-sm mb-2">🔄 Multi-Account Management</h3>
                <p className="text-cyan-300 text-sm mb-2">
                  You're managing campaigns from <strong>{accounts.length} Google Ads account{accounts.length !== 1 ? 's' : ''}</strong>.
                  Click <strong>"Sync from Google Ads"</strong> to refresh campaign data from all connected accounts.
                </p>
                <p className="text-cyan-300 text-sm">
                  <strong>Automation status</strong> shows whether weather-based bid adjustments are enabled for each campaign.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {showHelpModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 border-2 border-cyan-500 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-cyan-900/90 to-blue-900/90 backdrop-blur-sm p-6 border-b border-cyan-500 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <HelpCircle className="w-8 h-8 text-cyan-400" />
                  <h2 className="text-2xl font-bold text-white">How to Add Campaigns</h2>
                </div>
                <button
                  onClick={() => setShowHelpModal(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-300" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* What is "Sync from Google Ads"? */}
                <div className="bg-gradient-to-r from-blue-900/30 to-cyan-900/30 border-2 border-blue-500 rounded-lg p-5">
                  <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                    <HelpCircle className="w-6 h-6 text-blue-400" />
                    What Does "Sync from Google Ads" Do?
                  </h3>
                  <div className="space-y-3 text-gray-300">
                    <p className="text-base leading-relaxed">
                      Think of this button as a <strong className="text-cyan-400">"refresh" button</strong> that connects to your Google Ads account
                      and downloads a current list of all your advertising campaigns.
                    </p>
                    <div className="bg-cyan-900/30 border border-cyan-600 rounded-lg p-4">
                      <p className="text-sm text-cyan-200 font-semibold mb-2">📱 Real-World Example:</p>
                      <p className="text-sm text-gray-300">
                        Just like how your phone syncs your contacts from Google, this button syncs your advertising campaigns from Google Ads
                        so this weather automation system knows which campaigns exist and can manage them.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Method 1 */}
                <div className="bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border border-cyan-500/50 rounded-lg p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-2 bg-cyan-500/20 rounded-lg">
                      <RefreshCw className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">How to Sync Your Campaigns (Step-by-Step)</h3>
                      <p className="text-sm text-emerald-400">✓ Easiest Method - Takes 30 Seconds</p>
                    </div>
                  </div>

                  <p className="text-gray-300 mb-4 text-base leading-relaxed">
                    If you already have advertising campaigns running in your Google Ads account, here's how to import them into this system:
                  </p>

                  <div className="bg-gray-900/50 rounded-lg p-4 space-y-4">
                    <div className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-cyan-500 text-white text-base font-bold rounded-full flex-shrink-0">1</span>
                      <div className="flex-1">
                        <p className="text-white font-semibold mb-1">Click the "Sync from Google Ads" button</p>
                        <p className="text-sm text-gray-400">This is the blue button at the top of this page (next to "Discover Accounts")</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-cyan-500 text-white text-base font-bold rounded-full flex-shrink-0">2</span>
                      <div className="flex-1">
                        <p className="text-white font-semibold mb-1">Wait while the system connects to Google</p>
                        <p className="text-sm text-gray-400">You'll see a spinning icon and "Syncing..." message. This usually takes 10-30 seconds.</p>
                        <p className="text-xs text-amber-400 mt-1">💡 Don't refresh the page or click away - let it finish!</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-cyan-500 text-white text-base font-bold rounded-full flex-shrink-0">3</span>
                      <div className="flex-1">
                        <p className="text-white font-semibold mb-1">See your campaigns appear below</p>
                        <p className="text-sm text-gray-400">Once complete, all your campaigns will show up in the list. You'll see their names, budgets, and status (active/paused).</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-green-500 text-white text-base font-bold rounded-full flex-shrink-0">✓</span>
                      <div className="flex-1">
                        <p className="text-green-400 font-semibold mb-1">Done! Your campaigns are now ready</p>
                        <p className="text-sm text-gray-400">You can now create weather rules for any of these campaigns on the Weather Rules page.</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 bg-blue-900/30 border border-blue-500/50 rounded-lg p-4">
                    <p className="text-sm text-blue-300 font-semibold mb-2">💡 When Should I Click "Sync from Google Ads"?</p>
                    <ul className="text-sm text-gray-300 space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400 mt-0.5">→</span>
                        <span><strong className="text-white">Just created a new campaign in Google Ads?</strong> Sync to see it here</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400 mt-0.5">→</span>
                        <span><strong className="text-white">Changed a campaign's name or budget?</strong> Sync to update the information</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400 mt-0.5">→</span>
                        <span><strong className="text-white">First time using this system?</strong> Sync to import all your existing campaigns</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400 mt-0.5">→</span>
                        <span><strong className="text-white">Campaigns not showing up?</strong> Sync can fix missing campaign issues</span>
                      </li>
                    </ul>
                  </div>

                  <div className="mt-4 bg-green-900/30 border border-green-500/50 rounded-lg p-4">
                    <p className="text-sm text-green-300 font-semibold mb-2">✅ What Gets Synced (What You'll See)</p>
                    <ul className="text-sm text-gray-300 space-y-1">
                      <li className="flex items-start gap-2">
                        <span className="text-green-400">•</span>
                        <span><strong className="text-white">Campaign Name</strong> - The name you gave your campaign in Google Ads</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-400">•</span>
                        <span><strong className="text-white">Status</strong> - Whether it's active (green) or paused (gray)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-400">•</span>
                        <span><strong className="text-white">Daily Budget</strong> - How much you spend per day (shown in Rands)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-400">•</span>
                        <span><strong className="text-white">Campaign Type</strong> - Search, Display, Shopping, etc.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-green-400">•</span>
                        <span><strong className="text-white">Account Name</strong> - Which Google Ads account this campaign belongs to</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Method 2 */}
                <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/50 rounded-lg p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <Plus className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Method 2: Connect a New Google Ads Account</h3>
                      <p className="text-sm text-purple-400">For campaigns from different accounts</p>
                    </div>
                  </div>

                  <p className="text-gray-300 mb-4">
                    To add campaigns from a different Google Ads account:
                  </p>

                  <div className="bg-gray-900/50 rounded-lg p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-6 h-6 bg-purple-500 text-white text-sm font-bold rounded-full flex-shrink-0">1</span>
                      <div>
                        <p className="text-gray-300">Go to <button onClick={() => { setShowHelpModal(false); navigate('/settings'); }} className="text-purple-400 hover:text-purple-300 font-semibold underline">Settings page</button></p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-6 h-6 bg-purple-500 text-white text-sm font-bold rounded-full flex-shrink-0">2</span>
                      <p className="text-gray-300">Scroll to "Google Ads Connection" section</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-6 h-6 bg-purple-500 text-white text-sm font-bold rounded-full flex-shrink-0">3</span>
                      <p className="text-gray-300">Click <span className="text-purple-400 font-semibold">"Connect Google Ads Account"</span> button</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-6 h-6 bg-purple-500 text-white text-sm font-bold rounded-full flex-shrink-0">4</span>
                      <p className="text-gray-300">Login with Google and authorize access</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex items-center justify-center w-6 h-6 bg-purple-500 text-white text-sm font-bold rounded-full flex-shrink-0">5</span>
                      <p className="text-gray-300">Return here and click <span className="text-cyan-400 font-semibold">"Sync from Google Ads"</span></p>
                    </div>
                  </div>
                </div>

                {/* Current Setup */}
                <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-5">
                  <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Your Current Setup
                  </h3>
                  <div className="space-y-2">
                    <p className="text-gray-300">
                      <span className="text-gray-400">Connected Accounts:</span> <span className="text-white font-semibold">{accounts.length}</span>
                    </p>
                    <p className="text-gray-300">
                      <span className="text-gray-400">Total Campaigns:</span> <span className="text-white font-semibold">{totalCampaigns}</span>
                    </p>
                    {accounts.map((account) => (
                      <div key={account.customerId} className="ml-4 mt-2 p-3 bg-gray-800/50 border border-gray-700 rounded">
                        <p className="text-sm text-cyan-400 font-semibold">{account.displayName}</p>
                        <p className="text-xs text-gray-400">Customer ID: {account.customerId}</p>
                        <p className="text-xs text-gray-300 mt-1">{account.campaigns.length} campaigns synced</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Common Questions */}
                <div className="bg-purple-900/20 border border-purple-500/50 rounded-lg p-5">
                  <h3 className="text-lg font-bold text-purple-300 mb-4 flex items-center gap-2">
                    <HelpCircle className="w-5 h-5" />
                    Common Questions
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-white font-semibold mb-1">❓ Will this button create new campaigns for me?</p>
                      <p className="text-sm text-gray-300 ml-6">
                        <strong className="text-red-400">No.</strong> The "Sync" button only <strong>downloads</strong> campaigns that already exist in your Google Ads account.
                        You must create campaigns in Google Ads first (at ads.google.com), then sync them here.
                      </p>
                    </div>
                    <div>
                      <p className="text-white font-semibold mb-1">❓ Is it safe to click this button?</p>
                      <p className="text-sm text-gray-300 ml-6">
                        <strong className="text-green-400">Yes, 100% safe!</strong> The sync button only <strong>reads</strong> information from Google Ads.
                        It never changes, pauses, or deletes your campaigns. Think of it like viewing your bank balance - you're just looking, not spending.
                      </p>
                    </div>
                    <div>
                      <p className="text-white font-semibold mb-1">❓ How often should I sync?</p>
                      <p className="text-sm text-gray-300 ml-6">
                        Only when needed! Click "Sync" whenever you:
                        <span className="block mt-1 text-cyan-300">• Create a new campaign in Google Ads</span>
                        <span className="block text-cyan-300">• Change a campaign's name or budget</span>
                        <span className="block text-cyan-300">• Notice campaigns are missing from the list</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-white font-semibold mb-1">❓ Can I manage multiple Google Ads accounts?</p>
                      <p className="text-sm text-gray-300 ml-6">
                        <strong className="text-green-400">Yes!</strong> You can connect as many Google Ads accounts as you want.
                        Just use the "Connect Another Account" button, then sync. All campaigns from all accounts will show up in one place.
                      </p>
                    </div>
                    <div>
                      <p className="text-white font-semibold mb-1">❓ What if the sync fails or shows an error?</p>
                      <p className="text-sm text-gray-300 ml-6">
                        Try these solutions:
                        <span className="block mt-1 text-amber-300">1. Check your internet connection</span>
                        <span className="block text-amber-300">2. Wait 1 minute and try clicking "Sync" again</span>
                        <span className="block text-amber-300">3. If still failing, your Google Ads connection may have expired - reconnect in Settings</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Important Notes */}
                <div className="bg-amber-900/20 border-2 border-amber-500 rounded-lg p-5">
                  <h3 className="text-lg font-bold text-amber-300 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Important Things to Remember
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">🚫</span>
                      <div>
                        <p className="text-white font-semibold mb-1">This System Doesn't Create Campaigns</p>
                        <p className="text-sm text-gray-300">
                          Think of this weather automation system like a remote control for your TV - it can change channels (adjust bids)
                          but it can't build a new TV. You must create campaigns in Google Ads first, then sync them here to control them.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">🔒</span>
                      <div>
                        <p className="text-white font-semibold mb-1">Your Campaigns Are Safe</p>
                        <p className="text-sm text-gray-300">
                          The "Sync from Google Ads" button only <strong>reads</strong> data - it never deletes, pauses, or changes your campaigns.
                          The only time this system modifies campaigns is when weather rules trigger (and only if you enabled automation for that campaign).
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">⚡</span>
                      <div>
                        <p className="text-white font-semibold mb-1">Sync Takes 10-30 Seconds</p>
                        <p className="text-sm text-gray-300">
                          Be patient! The system needs time to connect to Google Ads and download all your campaign information.
                          Don't refresh the page or click the button multiple times - let it finish.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">🔄</span>
                      <div>
                        <p className="text-white font-semibold mb-1">Sync Updates, Not Replaces</p>
                        <p className="text-sm text-gray-300">
                          When you sync, existing campaigns get updated with latest info from Google Ads (new budget, new name, etc.).
                          Your weather rules and automation settings stay exactly as you configured them - sync doesn't reset anything.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-gray-900/90 backdrop-blur-sm p-4 border-t border-gray-700 flex justify-end">
                <button
                  onClick={() => setShowHelpModal(false)}
                  className="px-6 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold rounded-lg transition-all"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Campaign Details Modal */}
        {showDetailsModal && selectedCampaign && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 border-2 border-cyan-500 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-cyan-900/90 to-blue-900/90 backdrop-blur-sm p-6 border-b border-cyan-500 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Target className="w-8 h-8 text-cyan-400" />
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedCampaign.campaign_name}</h2>
                    <p className="text-sm text-gray-400">Campaign ID: {selectedCampaign.google_campaign_id}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-300" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Campaign Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 border border-cyan-500/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-5 h-5 text-cyan-400" />
                      <p className="text-sm text-gray-400">Status</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(selectedCampaign.status)}
                      <span className={`text-lg font-bold ${getStatusColor(selectedCampaign.status)}`}>
                        {getStatusText(selectedCampaign.status)}
                      </span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-emerald-900/30 to-green-900/30 border border-emerald-500/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-5 h-5 text-emerald-400" />
                      <p className="text-sm text-gray-400">Daily Budget</p>
                    </div>
                    <p className="text-2xl font-bold text-white">{formatBudget(selectedCampaign.budget_micros)}</p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-5 h-5 text-purple-400" />
                      <p className="text-sm text-gray-400">Weather Automation</p>
                    </div>
                    {loadingDetails ? (
                      <div className="flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />
                        <span className="text-sm text-gray-400">Loading...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {campaignRules.filter(r => r.is_active).length > 0 ? (
                          <>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-5 h-5 text-green-400" />
                              <span className="text-lg font-bold text-green-400">Active</span>
                            </div>
                            <p className="text-sm text-gray-300">
                              {campaignRules.filter(r => r.is_active).length} active rule{campaignRules.filter(r => r.is_active).length !== 1 ? 's' : ''}
                            </p>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <XCircle className="w-5 h-5 text-gray-400" />
                              <span className="text-lg font-bold text-gray-400">Inactive</span>
                            </div>
                            <p className="text-sm text-gray-500">No active rules</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Weather Rules Section */}
                <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Cloud className="w-6 h-6 text-cyan-400" />
                      <h3 className="text-xl font-bold text-white">Weather Rules</h3>
                    </div>
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        navigate('/rules');
                      }}
                      className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold rounded-lg transition-all text-sm"
                    >
                      Create Rule
                    </button>
                  </div>

                  {/* Template Summary */}
                  {!loadingDetails && campaignRules.length > 0 && (() => {
                    // Group rules by template
                    const templateGroups: Record<string, number> = {};
                    let customRuleCount = 0;

                    campaignRules.forEach(rule => {
                      const templateInfo = detectTemplate(rule.rule_name);
                      if (templateInfo) {
                        templateGroups[templateInfo.label] = (templateGroups[templateInfo.label] || 0) + 1;
                      } else {
                        customRuleCount++;
                      }
                    });

                    const hasTemplates = Object.keys(templateGroups).length > 0;

                    if (!hasTemplates && customRuleCount === 0) return null;

                    return (
                      <div className="mb-4 bg-gray-800/50 border border-gray-700 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-2">Rule Templates Active:</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(templateGroups).map(([label, count]) => {
                            const templateColor = label.includes('💎') ? 'purple'
                              : label.includes('🌧️') ? 'blue'
                              : label.includes('🌡️') ? 'orange'
                              : 'cyan';

                            const colorClasses = {
                              purple: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
                              blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
                              orange: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
                              cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
                            }[templateColor];

                            return (
                              <span
                                key={label}
                                className={`px-3 py-1 text-xs font-medium rounded-full border ${colorClasses}`}
                              >
                                {label} • {count} rule{count !== 1 ? 's' : ''}
                              </span>
                            );
                          })}
                          {customRuleCount > 0 && (
                            <span className="px-3 py-1 bg-gray-700 text-gray-300 text-xs font-medium rounded-full border border-gray-600">
                              ⚙️ Custom • {customRuleCount} rule{customRuleCount !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {loadingDetails ? (
                    <div className="text-center py-8">
                      <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-2" />
                      <p className="text-gray-400">Loading rules...</p>
                    </div>
                  ) : campaignRules.length === 0 ? (
                    <div className="text-center py-8 bg-gray-800/50 rounded-lg border border-gray-700">
                      <AlertCircle className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                      <p className="text-lg font-semibold text-white mb-2">No Weather Rules Yet</p>
                      <p className="text-sm text-gray-400 mb-4">
                        Create weather-based rules to automatically adjust bids for this campaign
                      </p>
                      <button
                        onClick={() => {
                          setShowDetailsModal(false);
                          navigate('/rules');
                        }}
                        className="px-6 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold rounded-lg transition-all"
                      >
                        Create First Rule
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {campaignRules.map((rule) => {
                        const templateInfo = detectTemplate(rule.rule_name);
                        const styles = getTemplateStyles(templateInfo);

                        return (
                        <div
                          key={rule.id}
                          className={`bg-gray-800/50 border-2 ${styles.border} rounded-lg p-4 transition-all ${templateInfo ? styles.bg : ''}`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-white">{rule.rule_name}</h4>

                              {/* Template Badge */}
                              {templateInfo && styles.badge && (
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles.badge}`}>
                                  {templateInfo.label}
                                </span>
                              )}

                              {/* Active/Inactive Badge */}
                              {rule.is_active ? (
                                <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
                                  Active
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs font-medium rounded-full">
                                  Inactive
                                </span>
                              )}
                            </div>

                            {/* Adjustment Type Badge */}
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              rule.adjustment_target === 'bid'
                                ? 'bg-cyan-500/20 text-cyan-400'
                                : 'bg-emerald-500/20 text-emerald-400'
                            }`}>
                              {rule.adjustment_target === 'bid' ? 'Bid Modifier' : 'Budget Change'}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-400">Condition:</p>
                              <p className="text-white font-medium">
                                {rule.metric} {rule.comparator} {rule.threshold_value}
                                {rule.metric?.includes('temp') ? '°C' : rule.metric?.includes('wind') ? ' km/h' : '%'}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-400">Action:</p>
                              <p className="text-white font-medium">
                                {rule.action_type === 'increase' ? '▲' : '▼'}
                                {rule.adjustment_target === 'budget'
                                  ? ` R${Math.abs(rule.bid_adjustment_percent ?? 0).toFixed(0)}`
                                  : ` ${Math.abs(rule.bid_adjustment_percent ?? 0)}%`
                                }
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-400">Location:</p>
                              <p className="text-white font-medium">{rule.location_name || 'Multiple'}</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Cooldown:</p>
                              <p className="text-white font-medium">{(rule.cooldown_minutes / 60).toFixed(1)}h</p>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-5">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-400" />
                    Quick Actions
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        navigate('/rules');
                      }}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold rounded-lg transition-all"
                    >
                      <Cloud className="w-5 h-5" />
                      Create Weather Rule
                    </button>
                    <a
                      href={`https://ads.google.com/aw/campaigns?campaignId=${selectedCampaign.google_campaign_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all"
                    >
                      <LinkIcon className="w-5 h-5" />
                      View in Google Ads
                    </a>
                  </div>
                </div>

                {/* Last Synced Info */}
                <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-sm text-blue-300">
                    <Clock className="w-4 h-4" />
                    <span>
                      Last synced: {selectedCampaign.last_synced_at
                        ? new Date(selectedCampaign.last_synced_at).toLocaleString()
                        : 'Never'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-gray-900/90 backdrop-blur-sm p-4 border-t border-gray-700 flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-6 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold rounded-lg transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
